import type { SxProps, Theme } from '@mui/material'

export const linkIconSx: SxProps<Theme> = { fontSize: '1.25rem' }

export const chevronLeftSx: SxProps<Theme> = { fontSize: 20 }

// StreamingMessageContent styles
export const streamingContentSx: SxProps<Theme> = {
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': { textDecoration: 'none' },
  },
}

export const blinkingCursorSx: SxProps<Theme> = {
  display: 'inline-block',
  width: '2px',
  height: '1em',
  bgcolor: 'green.dark',
  ml: '1px',
  verticalAlign: 'text-bottom',
  animation: 'blink 0.8s step-end infinite',
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
}

// ThinkingStepText styles
export const thinkingStepTextSx = (visible: boolean): SxProps<Theme> => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'success.main',
  opacity: visible ? 1 : 0,
  transition: 'opacity 200ms ease-in-out',
})

// ThinkingPreviewText styles
export const thinkingPreviewTextSx = (visible: boolean): SxProps<Theme> => ({
  fontSize: '0.75rem',
  color: 'grey.500',
  fontStyle: 'italic',
  opacity: visible ? 1 : 0,
  transition: 'opacity 200ms ease-in-out',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
})

// ThoughtAccordion styles
export const thoughtToggleSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  cursor: 'pointer',
  '&:hover .thought-label': { textDecoration: 'underline' },
}

export const thoughtExpandIconSx = (expanded: boolean): SxProps<Theme> => ({
  fontSize: '0.875rem',
  color: 'text.disabled',
  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: 'transform 0.2s',
})

export const thoughtLabelSx: SxProps<Theme> = {
  color: 'text.disabled',
  fontSize: '0.75rem',
}

export const thoughtContentSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.5,
  mt: 0.5,
  pl: 2.5,
}

// AssistantTab root
export const rootSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
}

// History view header
export const historyHeaderSx = (fullWidth: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  px: fullWidth ? 0 : 2,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'grey.200',
})

export const backButtonSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.5,
  borderRadius: '8px',
  cursor: 'pointer',
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
}

export const backButtonLabelSx: SxProps<Theme> = { fontWeight: 500 }

// Chat sub-header
export const chatSubHeaderSx = (fullWidth: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: fullWidth ? 0 : 2,
  py: 0.75,
  minHeight: fullWidth ? 'auto' : 64,
  borderBottom: fullWidth ? 0 : 1,
  borderColor: 'divider',
})

export const chatSubHeaderBackSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  ml: -0.5,
  px: 1,
  py: 0.5,
  borderRadius: '8px',
  cursor: 'pointer',
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
}

export const chatSubHeaderTitleSx: SxProps<Theme> = {
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

// Recent chats toggle
export const recentChatsSx = (isViewingChat: boolean): SxProps<Theme> => ({
  display: isViewingChat ? 'none' : 'block',
})

// Messages area
export const messagesAreaSx = (fullWidth: boolean): SxProps<Theme> => ({
  flex: 1,
  overflowY: 'auto',
  px: fullWidth ? 0 : 3,
  py: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
})

// Loading skeletons
export const skeletonRowEndSx: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-end' }
export const skeletonRowStartSx: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-start' }
export const skeletonUserBubbleSx: SxProps<Theme> = { maxWidth: '70%' }
export const skeletonRoundedSx: SxProps<Theme> = { borderRadius: '12px' }
export const skeletonAssistantBubbleSx: SxProps<Theme> = { maxWidth: '85%', width: '100%' }
export const skeletonAssistantLineSx: SxProps<Theme> = { borderRadius: '12px', mb: 1 }

// Welcome message
export const welcomeBoxSx: SxProps<Theme> = {
  bgcolor: 'grey.50',
  p: '16px',
  borderRadius: '12px',
  fontSize: '0.875rem',
  lineHeight: 1.6,
  color: 'text.primary',
}

export const messageHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontFamily: 'Outfit',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '18px',
  mb: '8px',
}

export const assistantLabelSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  color: 'green.dark',
}

export const timestampSx: SxProps<Theme> = {
  fontSize: '0.6875rem',
  fontWeight: 400,
  color: 'text.secondary',
}

// Message bubble
export const messageRowSx = (isUser: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isUser ? 'flex-end' : 'flex-start',
})

export const messageBubbleSx = (isUser: boolean): SxProps<Theme> => ({
  bgcolor: isUser ? 'primary.main' : 'grey.50',
  color: isUser ? 'white' : 'text.primary',
  p: '16px',
  borderRadius: '12px',
  maxWidth: '85%',
  fontSize: '0.875rem',
  lineHeight: 1.6,
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  '& pre': { whiteSpace: 'pre-wrap', overflowX: 'auto' },
  '& > *:last-child': { mb: 0 },
  '& p:last-child': { mb: 0 },
})

export const userLabelSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  color: 'rgba(255, 255, 255, 0.85)',
}

export const userTimestampSx: SxProps<Theme> = {
  fontSize: '0.6875rem',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.5)',
}

// Anchor link styles shared across message content
export const messageAnchorSx: SxProps<Theme> = {
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'none',
    },
  },
}

export const actionedMessageSx = (isActioned: boolean): SxProps<Theme> => ({
  ...messageAnchorSx,
  ...(isActioned && {
    '& button[data-action]': {
      pointerEvents: 'none',
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  }),
})

// Support ticket badge
export const supportTicketBadgeSx: SxProps<Theme> = {
  mt: 1,
  display: 'inline-flex',
  alignItems: 'center',
  px: 1.5,
  py: 0.5,
  bgcolor: 'success.light',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'green.dark',
}

// Streaming indicator
export const streamingContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
}

export const streamingIndicatorRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

export const hapanaPulseIconSx: SxProps<Theme> = {
  color: 'success.main',
  fontWeight: 'bold',
  animation: 'hapanaPulse 1.5s ease-in-out infinite',
  '@keyframes hapanaPulse': {
    '0%, 100%': { opacity: 0.4, transform: 'scale(0.95)' },
    '50%': { opacity: 1, transform: 'scale(1.1)' },
  },
}

// Quick action buttons area
export const quickActionsContainerSx = (fullWidth: boolean): SxProps<Theme> => ({
  px: fullWidth ? 0 : '24px',
  pt: 1,
  display: 'flex',
  gap: 0.75,
  flexWrap: 'wrap',
})

export const quickActionButtonSx = (isActive: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  gap: 0.5,
  px: 1.25,
  py: 0.5,
  borderRadius: '999px',
  border: '1px solid',
  borderColor: isActive ? 'primary.main' : 'grey.300',
  color: isActive ? 'primary.main' : 'text.secondary',
  bgcolor: 'transparent',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: 1.4,
  transition: 'all 0.15s ease',
  userSelect: 'none',
  '&:hover': {
    borderColor: 'primary.main',
    color: 'primary.main',
  },
})

export const quickActionIconSx: SxProps<Theme> = { fontSize: '0.875rem' }

export const popperSx: SxProps<Theme> = { zIndex: 1300 }

export const popperPaperSx: SxProps<Theme> = {
  borderRadius: 2,
  overflow: 'hidden',
  minWidth: 220,
  maxWidth: 320,
  py: 0.5,
}

export const popperItemSx: SxProps<Theme> = {
  px: 1.5,
  py: 1,
  fontSize: '0.8125rem',
  color: 'text.primary',
  cursor: 'pointer',
  lineHeight: 1.4,
  transition: 'background-color 0.1s ease',
  '&:hover': {
    bgcolor: 'action.hover',
  },
}

// Message input area
export const messageInputOuterSx = (fullWidth: boolean): SxProps<Theme> => ({
  px: fullWidth ? 0 : '24px',
  pt: '16px',
  pb: fullWidth ? '16px' : '24px',
})

export const messageInputBorderSx: SxProps<Theme> = {
  border: '2px solid',
  borderColor: 'grey.300',
  borderRadius: 3,
  '&:focus-within': {
    borderColor: 'primary.main',
  },
}

export const messageInputInnerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  px: 1.5,
  pt: 1.5,
  pb: 1,
}

export const attachmentButtonSx: SxProps<Theme> = { color: 'grey.500', p: 0 }

export const textFieldInputSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  lineHeight: 1.5,
  p: 0,
}

export const inputActionsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  pb: 1.5,
}

export const voiceButtonSx: SxProps<Theme> = {
  color: 'grey.500',
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: '50%',
}

export const sendButtonSx: SxProps<Theme> = {
  borderRadius: '999px',
  minWidth: 'auto',
  '&:focus, &:active': {
    color: 'white',
  },
}

// Ticket view
export const ticketViewSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
}

export const personOutlineIconSx: SxProps<Theme> = { fontSize: '16px' }
